/**
 * @fileoverview Page-setup side panel for the document canvas builder.
 * @module ui-admin/components/PageSetupPanel
 *
 * Responsibilities: - Let authors configure page size, orientation, margins, a decorative page
 *                     border, and a draft watermark, editing the `DocumentLayout.page` value.
 * Dependencies:     - React, lucide-react.
 *                   - @firesquirrel/document-render (PageSetup, BorderStyle, Spacing types).
 * Architecture:     - Controlled component: receives the current `PageSetup` and calls
 *                     `onChange(updated)` on every field change. No internal async operations.
 * Notes:            - Used by both tenant-web (DocumentTemplateBuilderPage) and system-web
 *                     (SystemDocumentBuilderPage). Mount it in a slide-out panel or modal; the
 *                     component itself is just the form body.
 *
 * @component PageSetupPanel
 * Purpose:     Form panel for editing DocumentLayout.page (size, orientation, margins, border,
 *              watermark).
 * Props:       page, onChange
 * Behavior:    Each control immediately calls onChange with the updated PageSetup. The caller is
 *              responsible for persisting the result.
 * Data Source: none (controlled by parent)
 */
import React from 'react';
import { X } from 'lucide-react';
import type { PageSetup, BorderStyle, Spacing } from '@firesquirrel/document-render';

export interface PageSetupPanelProps {
  /** Current page setup value. */
  page: PageSetup;
  /** Called with the updated PageSetup on every field change. */
  onChange: (page: PageSetup) => void;
  /** Optional close handler — renders an ✕ button when provided. */
  onClose?: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <span className="w-24 shrink-0 text-xs text-gray-500 pt-1.5">{label}</span>
      <div className="flex-1">{children}</div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">{title}</div>
      <div className="space-y-2.5">{children}</div>
    </div>
  );
}

function NumberInput({
  value, onChange, min = 0, max = 200, label,
}: { value: number; onChange: (v: number) => void; min?: number; max?: number; label: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-gray-500">{label}</span>
      <input
        type="number"
        min={min}
        max={max}
        value={value ?? 0}
        onChange={(e) => onChange(Math.max(min, Math.min(max, Number(e.target.value))))}
        className="w-full px-2 py-1 text-xs border border-gray-200 rounded-sm bg-white text-gray-800 focus:outline-hidden focus:ring-1 focus:ring-orange-400"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Default values
// ---------------------------------------------------------------------------
const DEFAULT_MARGINS: Spacing = { top: 20, right: 20, bottom: 20, left: 20 };
const DEFAULT_BORDER: BorderStyle = { width: 1, style: 'solid', color: '#000000', radius: 0 };

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * @component PageSetupPanel
 */
export default function PageSetupPanel({ page, onChange, onClose }: PageSetupPanelProps) {
  const margins = page.margins ?? DEFAULT_MARGINS;
  const border = page.border ?? null;
  const watermark = page.watermark ?? null;

  const update = (patch: Partial<PageSetup>) => onChange({ ...page, ...patch });
  const updateMargins = (patch: Partial<Spacing>) => update({ margins: { ...margins, ...patch } });
  const updateBorder = (patch: Partial<BorderStyle>) =>
    update({ border: border ? { ...border, ...patch } : { ...DEFAULT_BORDER, ...patch } });
  const updateWatermark = (patch: Partial<{ text: string; opacity: number; angle: number }>) =>
    update({ watermark: watermark ? { ...watermark, ...patch } : { text: 'DRAFT', opacity: 0.15, angle: -45, ...patch } });

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-900">Page Setup</h3>
        {onClose && (
          <button onClick={onClose} className="p-1 rounded-sm hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-4 space-y-5">

        {/* Page size & orientation */}
        <Section title="Page">
          <Row label="Size">
            <div className="flex gap-1.5">
              {(['letter', 'a4', 'legal'] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => update({ size: s })}
                  className={`flex-1 py-1.5 text-xs rounded border font-medium transition-colors ${
                    page.size === s
                      ? 'bg-orange-600 text-white border-orange-600'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-orange-300 hover:text-orange-600'
                  }`}
                >
                  {s === 'letter' ? 'Letter' : s === 'a4' ? 'A4' : 'Legal'}
                </button>
              ))}
            </div>
          </Row>
          <Row label="Orientation">
            <div className="flex gap-1.5">
              {(['portrait', 'landscape'] as const).map((o) => (
                <button
                  key={o}
                  type="button"
                  onClick={() => update({ orientation: o })}
                  className={`flex-1 py-1.5 text-xs rounded border font-medium transition-colors ${
                    page.orientation === o
                      ? 'bg-orange-600 text-white border-orange-600'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-orange-300 hover:text-orange-600'
                  }`}
                >
                  {o.charAt(0).toUpperCase() + o.slice(1)}
                </button>
              ))}
            </div>
          </Row>
        </Section>

        {/* Margins */}
        <Section title="Margins (mm)">
          <div className="grid grid-cols-2 gap-2">
            <NumberInput label="Top" value={margins.top ?? 20} onChange={(v) => updateMargins({ top: v })} />
            <NumberInput label="Bottom" value={margins.bottom ?? 20} onChange={(v) => updateMargins({ bottom: v })} />
            <NumberInput label="Left" value={margins.left ?? 20} onChange={(v) => updateMargins({ left: v })} />
            <NumberInput label="Right" value={margins.right ?? 20} onChange={(v) => updateMargins({ right: v })} />
          </div>
        </Section>

        {/* Page border */}
        <Section title="Page Border">
          <Row label="Enabled">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!border}
                onChange={(e) => update({ border: e.target.checked ? DEFAULT_BORDER : null })}
                className="w-4 h-4 rounded-sm border-gray-300 text-orange-600 focus:ring-orange-400"
              />
              <span className="text-xs text-gray-600">Add decorative border</span>
            </label>
          </Row>
          {border && (
            <>
              <Row label="Width (pt)">
                <NumberInput label="" value={border.width} onChange={(v) => updateBorder({ width: v })} min={1} max={20} />
              </Row>
              <Row label="Style">
                <select
                  value={border.style}
                  onChange={(e) => updateBorder({ style: e.target.value as BorderStyle['style'] })}
                  className="w-full px-2 py-1 text-xs border border-gray-200 rounded-sm bg-white text-gray-800 focus:outline-hidden focus:ring-1 focus:ring-orange-400"
                >
                  <option value="solid">Solid</option>
                  <option value="dashed">Dashed</option>
                  <option value="dotted">Dotted</option>
                  <option value="double">Double</option>
                </select>
              </Row>
              <Row label="Colour">
                <input
                  type="color"
                  value={border.color}
                  onChange={(e) => updateBorder({ color: e.target.value })}
                  className="w-full h-7 p-0.5 border border-gray-200 rounded-sm cursor-pointer"
                />
              </Row>
              <Row label="Corner radius">
                <NumberInput label="" value={border.radius ?? 0} onChange={(v) => updateBorder({ radius: v })} min={0} max={40} />
              </Row>
            </>
          )}
        </Section>

        {/* Watermark */}
        <Section title="Watermark">
          <Row label="Enabled">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!watermark}
                onChange={(e) => update({ watermark: e.target.checked ? { text: 'DRAFT', opacity: 0.15, angle: -45 } : null })}
                className="w-4 h-4 rounded-sm border-gray-300 text-orange-600 focus:ring-orange-400"
              />
              <span className="text-xs text-gray-600">Show watermark</span>
            </label>
          </Row>
          {watermark && (
            <>
              <Row label="Text">
                <input
                  type="text"
                  value={watermark.text}
                  onChange={(e) => updateWatermark({ text: e.target.value })}
                  placeholder="DRAFT"
                  className="w-full px-2 py-1 text-xs border border-gray-200 rounded-sm bg-white text-gray-800 focus:outline-hidden focus:ring-1 focus:ring-orange-400"
                />
              </Row>
              <Row label="Opacity">
                <div className="flex items-center gap-2">
                  <input
                    type="range"
                    min={0.05}
                    max={0.5}
                    step={0.01}
                    value={watermark.opacity}
                    onChange={(e) => updateWatermark({ opacity: Number(e.target.value) })}
                    className="flex-1 accent-orange-600"
                  />
                  <span className="text-xs text-gray-500 w-8 text-right">{Math.round(watermark.opacity * 100)}%</span>
                </div>
              </Row>
              <Row label="Angle (°)">
                <NumberInput label="" value={watermark.angle} onChange={(v) => updateWatermark({ angle: v })} min={-90} max={90} />
              </Row>
            </>
          )}
        </Section>
      </div>
    </div>
  );
}
